import { Alert } from "@components/ui/Alert"
import { Button } from "@components/ui/Button"
import { Dialog } from "@components/ui/Dialog"
import { AreaSpinner } from "@components/ui/Spinner"
import { SquadupEmbed } from "@features/ticketing/SquadUpEmbed"
import { useSquadup, useSquadupSubscriber } from "@features/ticketing/useSquadUp"
import { useRPCMutation, useRPCQuery } from "eddev/hooks"
import { AnimatePresence, motion } from "motion/react"
import { ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { useInterval } from "react-use"
import { proxy, useSnapshot } from "valtio"
import { AccountDetails } from "../../../server/routes/api.memberships"
import { useAuth } from "../useAuth"
import { AccountDetailsForm, MyInfoForm } from "./AccountDetailsForm"
import { useLoginForm } from "./LoginScreen"
import { MembershipDetails } from "./MembershipDetails"
import { Field } from "@components/ui/Field"
import { Input } from "@components/ui/Input"
import { alerts } from "@utils/alerts"
import { SquadupUtils } from "@features/ticketing/squadup/squadup-utils"

type Props = {
  eventId: number
}

function createSignupStore() {
  const store = proxy({
    confirmedEmail: "",
    hasAccount: false,
    hasPaid: false,
    createPasswordState: "idle" as "idle" | "loading" | "please-confirm",
    setConfirmedEmail(email: string, hasAccount: boolean) {
      store.confirmedEmail = email
      store.hasAccount = hasAccount
      if (store.confirmedDetails?.email !== email) {
        store.confirmedDetails = null
      }
    },
    confirmedDetails: null as null | AccountDetails,
    setConfirmedDetails(details: AccountDetails) {
      store.confirmedDetails = details
    },
    setHasPaid(hasPaid: boolean) {
      store.hasPaid = hasPaid
    },
    setPasswordState(state: "idle" | "loading" | "please-confirm") {
      store.createPasswordState = state
    },
  })

  return store
}

export function MemberSignup(props: Props) {
  const state = useSnapshot(useMemo(createSignupStore, []))
  const auth = useAuth()

  const squadup = useSquadup()

  useEffect(() => {
    squadup.close()
  }, [])

  // const [email, setEmail] = useState("")

  const detailsFormRef = useRef<MyInfoForm>(null!)
  const [password, setPassword] = useState("")

  // const confirmEmail = useRPCMutation("api.memberships.preLogin")

  const accountLookup = useRPCQuery(
    "api.memberships.getAccount",
    { jwt: auth.session?.access_token! },
    {
      staleTime: 1,
      refetchInterval: 0,
      enabled: auth.loggedIn,
    },
  )

  const confirmOrder = useRPCMutation("api.memberships.confirmMembershipOrder")

  const loginForm = useLoginForm({
    loginType: "signup",
    onNoAccountFound: (email) => {
      state.setConfirmedEmail(email, false)
    },
  })

  // If the user enters a logged in state, set their email as confirmed
  useEffect(() => {
    if (auth.session?.user.email) {
      state.setConfirmedEmail(auth.session?.user.email, true)
    } else {
      state.setConfirmedEmail("", false)
    }
  }, [auth.session?.user.email])

  let title: ReactNode = "Become a member"
  let description: ReactNode = null
  let contents: ReactNode = null
  let buttons: ReactNode = null
  let onSubmit = () => {}
  let key = "default"
  let padded = true

  if (!state.confirmedEmail && !auth.loggedIn) {
    title = "Become a member"
    description = "Let's start with your email address"
    contents = (
      <div className="stack-y-2">
        <div className="type-label-m">{loginForm.title}</div>
        {loginForm.showing.message && <Alert>{loginForm.showing.message}</Alert>}
        {loginForm.showing.error && <Alert error>{loginForm.showing.error}</Alert>}
        {loginForm.fields}
      </div>
    )
    buttons = loginForm.button
    onSubmit = loginForm.submit
    key = loginForm.showing.mode
  } else if (auth.loggedIn && accountLookup.isPending) {
    contents = <AreaSpinner />
  } else if (auth.loggedIn && !accountLookup.data?.membership?.canRenew) {
    title = "Your Membership"
    description = "You're already a member!"
    contents = <MembershipDetails extended />
    buttons = (
      <Button
        onClick={() => {
          auth.closeModal()
        }}
      >
        Done
      </Button>
    )
  } else if (state.confirmedEmail && !state.confirmedDetails) {
    title = "Your Details"
    description = "Please confirm your details below"
    contents = (
      <div className="stack-y-2">
        {accountLookup.isFetching ? (
          <AreaSpinner />
        ) : (
          <AccountDetailsForm
            account={{
              email: state.confirmedEmail,
              firstName: "",
              lastName: "",
              ...(accountLookup.data?.details ?? {}),
            }}
            membership={undefined}
            formRef={detailsFormRef}
            hideEmailField
          />
        )}
      </div>
    )
    if (!accountLookup.isFetching) {
      buttons = (
        <>
          <Button type="submit">Continue</Button>
        </>
      )
    }
    onSubmit = () => {
      state.setConfirmedDetails(detailsFormRef.current.confirm())
    }
  } else if (!state.hasPaid && state.confirmedEmail) {
    padded = false
    contents = (
      <div className="squadup-dialog-wrapper member-signup">
        <SquadupMemberSignup
          eventId={props.eventId}
          email={state.confirmedEmail}
          onSignup={(orderId) => {
            state.setHasPaid(true)
            if (!confirmOrder.isSuccess) {
              confirmOrder.mutateAsync({
                eventId: Number(props.eventId),
                details: state.confirmedDetails!,
                orderId: orderId,
              })
            }
          }}
        />
      </div>
    )
  } else if (state.hasPaid) {
    if (confirmOrder.isPending) {
      contents = <AreaSpinner />
    } else if (confirmOrder.isError) {
      contents = <Alert error>{confirmOrder.error.message}</Alert>
    } else if (!confirmOrder.data?.found) {
      contents = <Alert error>We're having trouble confirming your order.</Alert>
    } else if (confirmOrder.data.found) {
      if (auth.loggedIn) {
        title = "Your Membership"
        contents = <MembershipDetails />
        contents = <MembershipDetails extended />
        buttons = (
          <Button
            onClick={() => {
              auth.closeModal()
            }}
          >
            Done
          </Button>
        )
      } else {
        if (state.createPasswordState === "please-confirm") {
          title = "Thank you for joining"
          description = "You're all set!"
          contents = "Please check your email for a confirmation link."
          buttons = (
            <Button
              onClick={() => {
                auth.closeModal()
              }}
            >
              Done
            </Button>
          )
        } else {
          title = "Thank you for your payment"
          description = "Please now choose a password"
          contents = (
            <div className="stack-y-2">
              <Field label="Choose your password" required>
                <Input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onValueChange={setPassword}
                  disabled={state.createPasswordState === "loading"}
                />
              </Field>
            </div>
          )
          buttons = (
            <Button type="submit" loading={state.createPasswordState === "loading"}>
              Done
            </Button>
          )
          onSubmit = async () => {
            state.setPasswordState("loading")
            auth.api.auth
              .signUp({
                email: state.confirmedEmail,
                password: password,
              })
              .then((result) => {
                if (result.error) {
                  alerts.displayAlert({
                    title: "Error",
                    description: result.error.message,
                  })
                  state.setPasswordState("idle")
                } else if (result.data) {
                  if (result.data.session) {
                    auth.api.auth.setSession(result.data.session)
                  }
                  state.setPasswordState("please-confirm")
                }
              })
          }
        }
      }
    }
  }

  return (
    <Dialog
      open
      padded={false}
      onOpenChange={(open) => {
        if (!open) {
          auth.closeModal()
        }
      }}
      onSubmit={onSubmit}
      className="w-dialogW"
    >
      {(title || description) && (
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>
      )}
      <Dialog.Scroller padded={padded}>
        <AnimatePresence mode="wait" initial={false}>
          {contents && (
            <motion.div key={key} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {contents}
            </motion.div>
          )}
        </AnimatePresence>
      </Dialog.Scroller>
      {buttons && <Dialog.Footer>{buttons}</Dialog.Footer>}
    </Dialog>
  )
}

function SquadupMemberSignup(props: { eventId: number; email: string; onSignup: (orderId: number) => void }) {
  const squadup = useSquadup()
  const [state, setState] = useState("idle")

  useEffect(() => {
    squadup.open({
      eventId: props.eventId,
      shoppingCartEnabled: false,
      orderQuestions: [],
      successCallback: (data) => {
        if (data.items[0].order_id) {
          props.onSignup(data.items[0].order_id)
        }
      },
    })
  }, [])

  useEffect(() => {
    return () => {
      squadup.close()
    }
  }, [])

  useInterval(() => {
    // Pre-fill email
    const emailField = squadup.element.querySelector(".form-group:has(#user-email)") as HTMLElement
    if (emailField && props.email) {
      // emailField.style.display = "none"
      const input = emailField.querySelector("#user-email") as HTMLInputElement
      if (input.value !== props.email) {
        input.disabled = true
        SquadupUtils.triggerChange(input, props.email)
      }
    }

    // Hide password
    const passwordField = squadup.element.querySelector(".form-group:has(#user-password)") as HTMLElement
    if (passwordField) {
      passwordField.style.display = "none"
    }
  }, 300)

  return (
    <div>
      <SquadupEmbed
        enabled
        name="member-signup"
        onVisibleChange={(visible, loading) => setState(loading ? "loading" : visible ? "expanded" : "idle")}
        getPriority={(state) => 100}
      />
    </div>
  )
}
